import { cn } from "design-system/utils"
import { ChevronDown, ChevronUp, ShoppingCart, Star } from "lucide-react"
import { useState } from "react"
import { useTranslation } from "react-i18next"

export type ProductCardProps = {
  id: string
  title: string
  description?: string | null
  imageUrl?: string | null
  price?: { amount: number; currency: string }
  compareAtPrice?: { amount: number; currency: string }
  priceRange?: { min: number; max: number; currency: string } | null
  url?: string | null
  options?: Array<{ name: string; values: string[] }>
  rating?: { rating: number; count: number } | null
}

export function ProductCard({
  title,
  imageUrl,
  price,
  compareAtPrice,
  priceRange,
  url,
  options,
  rating,
}: ProductCardProps) {
  const { t, i18n } = useTranslation()
  const [showOptions, setShowOptions] = useState(false)
  const primaryUrl = url
  const locale = i18n.language
  const visibleOptions = (options ?? []).filter((option) => option.values.length > 0)
  const isInlineOptions = visibleOptions.length === 1
  const hasAccordionOptions = visibleOptions.length > 1

  function formatPrice(price: number, currency: string) {
    return new Intl.NumberFormat(locale, { style: "currency", currency }).format(price)
  }

  function renderPrice() {
    const lowestPrice =
      price ?? (priceRange ? { amount: priceRange.min, currency: priceRange.currency } : null)
    if (!lowestPrice) return null

    return (
      <span className="flex items-baseline gap-1 text-xs font-semibold">
        {compareAtPrice && compareAtPrice.amount > lowestPrice.amount && (
          <span className="font-normal text-muted-foreground/70 line-through">
            {formatPrice(compareAtPrice.amount, compareAtPrice.currency)}
          </span>
        )}
        <span>{formatPrice(lowestPrice.amount, lowestPrice.currency)}</span>
      </span>
    )
  }

  function renderRating() {
    if (!rating) return null
    return (
      <div className="flex items-center gap-1 text-xs text-muted-foreground">
        <Star className="size-3 fill-chart-primary text-chart-primary" />
        <span>{rating.rating.toFixed(1)}</span>
        <span>
          {"("}
          {rating.count}
          {")"}
        </span>
      </div>
    )
  }

  function renderOptions() {
    if (isInlineOptions) {
      const inlineOption = visibleOptions[0]!
      const visibleValues = inlineOption.values.slice(0, 3)
      const remainingCount = inlineOption.values.length - visibleValues.length
      return (
        <div className="flex flex-wrap gap-1 pt-0.5">
          {visibleValues.map((value) => (
            <span
              key={value}
              className="text-[10px] px-1.5 py-0.5 rounded bg-muted text-muted-foreground"
            >
              {value}
            </span>
          ))}
          {remainingCount > 0 && (
            <span className="text-[10px] px-1.5 py-0.5 rounded bg-muted text-muted-foreground">
              {"+"}
              {remainingCount}
            </span>
          )}
        </div>
      )
    }

    if (!hasAccordionOptions) return null

    return (
      <>
        <button
          onClick={(e) => {
            e.preventDefault()
            e.stopPropagation()
            setShowOptions(!showOptions)
          }}
          className="flex items-center gap-1 text-[10px] text-muted-foreground hover:text-foreground transition-colors self-start cursor-pointer"
        >
          {showOptions ? (
            <>
              <ChevronUp className="size-3" />
              {t("conversations.chat.hideOptions")}
            </>
          ) : (
            <>
              <ChevronDown className="size-3" />
              {t("conversations.chat.viewOptions")}
            </>
          )}
        </button>
        <div
          className={`overflow-hidden transition-all duration-300 ease-in-out ${
            showOptions ? "max-h-[500px] opacity-100" : "max-h-0 opacity-0"
          }`}
        >
          <div className="flex flex-col gap-1.5 pt-1">
            {visibleOptions.map((option) => {
              const visibleValues = option.values.slice(0, 3)
              const remainingCount = option.values.length - visibleValues.length
              return (
                <div key={option.name} className="flex flex-col gap-0.5">
                  <span className="text-[10px] font-medium text-muted-foreground">
                    {option.name}
                  </span>
                  <div className="flex flex-wrap gap-1">
                    {visibleValues.map((value) => (
                      <span
                        key={value}
                        className="text-[10px] px-1.5 py-0.5 rounded bg-muted text-muted-foreground"
                      >
                        {value}
                      </span>
                    ))}
                    {remainingCount > 0 && (
                      <span className="text-[10px] px-1.5 py-0.5 rounded bg-muted text-muted-foreground">
                        {"+"}
                        {remainingCount}
                      </span>
                    )}
                  </div>
                </div>
              )
            })}
          </div>
        </div>
      </>
    )
  }

  const cardContent = (
    <>
      {imageUrl ? (
        <div className="h-36 bg-muted overflow-hidden">
          <img src={imageUrl} alt={title} className="w-full h-full object-cover" />
        </div>
      ) : (
        <div className="h-36 bg-muted flex items-center justify-center">
          <ShoppingCart className="size-8 text-muted-foreground opacity-30" />
        </div>
      )}
      <div className="flex flex-col gap-1.5 p-3 pb-2 flex-1">
        <span className="text-xs font-semibold leading-snug line-clamp-2" title={title}>
          {title}
        </span>
        {renderRating()}
        {renderPrice()}
        {renderOptions()}
      </div>
    </>
  )

  const baseClasses =
    "flex flex-col w-44 shrink-0 rounded-xl bg-card overflow-hidden text-card-foreground shadow-xs"

  if (primaryUrl) {
    return (
      <a
        href={primaryUrl}
        target="_blank"
        rel="noopener noreferrer"
        className={cn(baseClasses, "hover:shadow-md transition-shadow cursor-pointer")}
      >
        {cardContent}
      </a>
    )
  }

  return <div className={baseClasses}>{cardContent}</div>
}
